import { useState, useEffect, useMemo } from 'react';
import { trackEvent } from '../utils/analytics';
import { API_ENDPOINTS, HOME_SURVEY_POPUP } from '../config/constants';

function isEmptySurveyAnswer(value) {
  return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

function ChatSurveyQuestionInput({ question, value, onChange }) {
  const {
    question_type: questionType,
    options = [],
    rating_max: ratingMax = 5,
  } = question;

  if (questionType === 'multiple_choice' || questionType === 'dropdown') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {options.map((option) => (
          <label key={option.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.82rem', color: '#e8eef3' }}>
            <input
              type="radio"
              name={`chat_survey_${question.id}`}
              checked={value === option.id}
              onChange={() => onChange(question.id, option.id)}
            />
            <span>{option.option_text}</span>
          </label>
        ))}
      </div>
    );
  }

  if (questionType === 'checkbox') {
    const current = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {options.map((option) => {
          const checked = current.includes(option.id);
          return (
            <label key={option.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '0.82rem', color: '#e8eef3', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(event) => {
                  const next = event.target.checked
                    ? [...current, option.id]
                    : current.filter((optionId) => optionId !== option.id);
                  onChange(question.id, next);
                }}
                style={{ width: '16px', height: '16px', accentColor: '#da291c', cursor: 'pointer' }}
              />
              <span>{option.option_text}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (questionType === 'rating') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {Array.from({ length: ratingMax }, (_, index) => index + 1).map((tick) => (
          <button
            key={tick}
            type="button"
            onClick={() => onChange(question.id, String(tick))}
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '8px',
              border: value === String(tick) ? '1px solid #da291c' : '1px solid #4d5a68',
              background: value === String(tick) ? '#3d1f21' : '#1b2631',
              color: value === String(tick) ? '#ffe4e2' : '#d4dde6',
              cursor: 'pointer',
              fontSize: '0.82rem'
            }}
          >
            {tick}
          </button>
        ))}
      </div>
    );
  }

  if (questionType === 'yes_no') {
    return (
      <div style={{ display: 'flex', gap: '10px' }}>
        {['Yes', 'No'].map((option) => (
          <label key={option} style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '0.82rem', color: '#e8eef3' }}>
            <input
              type="radio"
              name={`chat_survey_${question.id}`}
              checked={value === option}
              onChange={() => onChange(question.id, option)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    );
  }

  if (questionType === 'date') {
    return (
      <input
        type="date"
        value={value || ''}
        onChange={(event) => onChange(question.id, event.target.value)}
        style={{ width: '100%', border: '1px solid #4d5a68', borderRadius: '8px', padding: '8px', background: '#101821', color: '#e8eef3' }}
      />
    );
  }

  if (questionType === 'textarea') {
    return (
      <textarea
        rows={2}
        value={value || ''}
        onChange={(event) => onChange(question.id, event.target.value)}
        style={{ width: '100%', border: '1px solid #4d5a68', borderRadius: '8px', padding: '8px', resize: 'vertical', background: '#101821', color: '#e8eef3' }}
      />
    );
  }

  return (
    <input
      type="text"
      value={value || ''}
      onChange={(event) => onChange(question.id, event.target.value)}
      style={{ width: '100%', border: '1px solid #4d5a68', borderRadius: '8px', padding: '8px', background: '#101821', color: '#e8eef3' }}
    />
  );
}

function Chatbot({
  buttonLabel = 'Ask Chai',
  subtitle = 'Your Mentor to Lead With Tech',
  position = 'right',
  autoOpen = true
}) {
  const configuredSurveyIds = useMemo(() => {
    const idsFromCsv = String(HOME_SURVEY_POPUP?.surveyIdsCsv || '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isInteger(value) && value > 0);

    if (idsFromCsv.length) return idsFromCsv;

    const fallbackId = Number(HOME_SURVEY_POPUP?.surveyId);
    return Number.isInteger(fallbackId) && fallbackId > 0 ? [fallbackId] : [];
  }, []);

  const surveyId = useMemo(() => {
    if (!configuredSurveyIds.length) return null;
    const randomIndex = Math.floor(Math.random() * configuredSurveyIds.length);
    return configuredSurveyIds[randomIndex];
  }, [configuredSurveyIds]);

  const surveyEnabled = !!HOME_SURVEY_POPUP?.enabled && !!surveyId;
  const surveySubmittedStorageKey = surveyId ? `askchai_survey_submitted_${surveyId}` : null;

  const [showChatbot, setShowChatbot] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyError, setSurveyError] = useState('');
  const [surveyName, setSurveyName] = useState('');
  const [surveyEmail, setSurveyEmail] = useState('');
  const [surveyFieldErrors, setSurveyFieldErrors] = useState({});
  const [surveyFormError, setSurveyFormError] = useState('');
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const brandIconSrc = '/images/lwt.png';

  useEffect(() => {
    if (autoOpen) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      if (!isMobile) {
        setTimeout(() => setShowChatbot(true), 1500);
      }
    }
  }, [autoOpen]);

  useEffect(() => {
    if (!surveyEnabled || !surveySubmittedStorageKey) return;

    if (localStorage.getItem(surveySubmittedStorageKey) === '1') {
      setSurveySubmitted(true);
      return;
    }

    let isCancelled = false;

    const loadSurvey = async () => {
      try {
        setSurveyLoading(true);
        setSurveyError('');
        const response = await fetch(API_ENDPOINTS.SURVEY_TAKE(surveyId));
        const data = await response.json();

        if (!isCancelled && response.ok && data.success && data.data?.questions?.length) {
          setSurveyData(data.data);
          trackEvent({
            event: 'askchai_survey_view',
            category: 'Survey',
            label: `survey_${surveyId}`,
          });
        } else if (!isCancelled) {
          setSurveyError('Survey is currently unavailable.');
        }
      } catch (error) {
        console.error('Unable to load Ask Chai survey:', error);
        if (!isCancelled) {
          setSurveyError('Unable to load survey right now.');
        }
      } finally {
        if (!isCancelled) {
          setSurveyLoading(false);
        }
      }
    };

    loadSurvey();

    return () => {
      isCancelled = true;
    };
  }, [surveyEnabled, surveyId, surveySubmittedStorageKey]);

  const handleSurveyAnswerChange = (questionId, value) => {
    setSurveyAnswers((previous) => ({ ...previous, [questionId]: value }));
    setSurveyFieldErrors((previous) => ({ ...previous, [questionId]: undefined }));
  };

  const validateSurvey = () => {
    const errors = {};

    if (!surveyName.trim()) errors._name = 'Name is required';
    if (!surveyEmail.trim()) {
      errors._email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(surveyEmail)) {
      errors._email = 'Please enter a valid email';
    }

    (surveyData?.questions || []).forEach((question) => {
      if (question.is_required && isEmptySurveyAnswer(surveyAnswers[question.id])) {
        errors[question.id] = 'This question is required';
      }
    });

    setSurveyFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSurveySubmit = async (event) => {
    event.preventDefault();
    setSurveyFormError('');

    if (!surveyData || !validateSurvey()) {
      setSurveyFormError('Please complete required fields before submitting.');
      return;
    }

    const answersPayload = (surveyData.questions || [])
      .filter((question) => !isEmptySurveyAnswer(surveyAnswers[question.id]))
      .map((question) => {
        const value = surveyAnswers[question.id];
        if (question.question_type === 'checkbox') {
          return { question_id: question.id, option_ids: value };
        }
        if (question.question_type === 'multiple_choice' || question.question_type === 'dropdown') {
          return { question_id: question.id, option_id: value };
        }
        return { question_id: question.id, answer_text: String(value) };
      });

    const payload = {
      respondent_name: surveyName.trim(),
      respondent_email: surveyEmail.trim().toLowerCase(),
      answers: answersPayload,
    };

    try {
      setSurveySubmitting(true);
      const response = await fetch(API_ENDPOINTS.SURVEY_RESPOND(surveyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setSurveySubmitted(true);
        localStorage.setItem(surveySubmittedStorageKey, '1');
        trackEvent({
          event: 'askchai_survey_submit',
          category: 'Survey',
          label: `survey_${surveyId}`,
        });
      } else {
        setSurveyFormError(data.message || 'Could not submit survey right now.');
      }
    } catch (error) {
      setSurveyFormError('Unable to submit survey right now. Please try again.');
    } finally {
      setSurveySubmitting(false);
    }
  };

  const positionStyles = position === 'left' ? {
    bottom: '24px',
    left: showChatbot ? '24px' : '-400px',
  } : {
    bottom: '24px',
    right: showChatbot ? '24px' : '100px',
  };

  return (
    <div style={{
      position: 'fixed',
      ...positionStyles,
      zIndex: 1001,
      transition: 'all 0.3s ease'
    }}>
      {!showChatbot && (
        <button
          onClick={() => setShowChatbot(true)}
          style={{
            background: 'linear-gradient(135deg, #DA291C 0%, #b81f14 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '50px',
            padding: '16px 24px',
            fontSize: '1rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(218, 41, 28, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease'
          }}
        >
          <img
            src={brandIconSrc}
            alt="Lead With Tech"
            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
          />
          {buttonLabel}
        </button>
      )}

      {showChatbot && (
        <div style={{
          width: '380px',
          height: 'auto',
          maxHeight: '560px',
          background: '#14202b',
          border: '1px solid #2d3a46',
          borderRadius: '16px',
          boxShadow: '0 14px 38px rgba(0, 0, 0, 0.38)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #273545 0%, #1a2631 52%, #b5241d 100%)',
            color: '#fff',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img
                src={brandIconSrc}
                alt="Lead With Tech"
                style={{ width: '36px', height: '36px', objectFit: 'contain' }}
              />
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Your Say Matters</h3>
                <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.9 }}>{subtitle}</p>
              </div>
            </div>
            <button
              onClick={() => setShowChatbot(false)}
              style={{
                background: 'rgba(232, 238, 243, 0.16)',
                border: '1px solid rgba(233, 117, 110, 0.55)',
                color: '#fff',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '1.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              x
            </button>
          </div>

          <div style={{
            overflowY: 'auto',
            maxHeight: '460px',
            background: 'linear-gradient(180deg, #15202b 0%, #101821 100%)',
            paddingBottom: '12px'
          }}>
            <div style={{
              margin: '12px',
              border: '1px solid #3a4958',
              borderRadius: '14px',
              background: 'linear-gradient(155deg, #1f2b36 0%, #18232d 100%)',
              padding: '12px'
            }}>
              {surveyLoading && <p style={{ margin: 0, fontSize: '0.84rem', color: '#b9c7d4' }}>Loading survey...</p>}

              {!surveyLoading && surveySubmitted && (
                <p style={{ margin: 0, fontSize: '0.84rem', color: '#8be2a3', fontWeight: 600 }}>
                  Thank you. Your response has already been submitted.
                </p>
              )}

              {!surveyLoading && !surveySubmitted && surveyError && (
                <p style={{ margin: 0, fontSize: '0.84rem', color: '#ff8d87' }}>{surveyError}</p>
              )}

              {!surveyLoading && !surveySubmitted && surveyData && (
                <form onSubmit={handleSurveySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {surveyData.description && (
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#ccd7e1', lineHeight: 1.4 }}>
                      {surveyData.description}
                    </p>
                  )}

                  <input
                    type="text"
                    placeholder="Name *"
                    value={surveyName}
                    onChange={(event) => {
                      setSurveyName(event.target.value);
                      setSurveyFieldErrors((previous) => ({ ...previous, _name: undefined }));
                    }}
                    style={{ width: '100%', border: '1px solid #4d5a68', borderRadius: '8px', padding: '7px', background: '#101821', color: '#e8eef3' }}
                  />
                  {surveyFieldErrors._name && <p style={{ margin: 0, color: '#ff8d87', fontSize: '0.76rem' }}>{surveyFieldErrors._name}</p>}

                  <input
                    type="email"
                    placeholder="Email *"
                    value={surveyEmail}
                    onChange={(event) => {
                      setSurveyEmail(event.target.value);
                      setSurveyFieldErrors((previous) => ({ ...previous, _email: undefined }));
                    }}
                    style={{ width: '100%', border: '1px solid #4d5a68', borderRadius: '8px', padding: '7px', background: '#101821', color: '#e8eef3' }}
                  />
                  {surveyFieldErrors._email && <p style={{ margin: 0, color: '#ff8d87', fontSize: '0.76rem' }}>{surveyFieldErrors._email}</p>}

                  {(surveyData.questions || []).map((question) => (
                    <div key={question.id} style={{ border: '1px solid #445361', borderRadius: '10px', padding: '8px', background: '#1c2732' }}>
                      <p style={{ margin: '0 0 6px', fontSize: '0.82rem', color: '#e8eef3', fontWeight: 600 }}>
                        {question.question_text}{question.is_required ? ' *' : ''}
                      </p>
                      <ChatSurveyQuestionInput
                        question={question}
                        value={surveyAnswers[question.id]}
                        onChange={handleSurveyAnswerChange}
                      />
                      {surveyFieldErrors[question.id] && (
                        <p style={{ margin: '6px 0 0', color: '#ff8d87', fontSize: '0.75rem' }}>{surveyFieldErrors[question.id]}</p>
                      )}
                    </div>
                  ))}

                  {surveyFormError && <p style={{ margin: 0, color: '#ff8d87', fontSize: '0.76rem' }}>{surveyFormError}</p>}

                  <button
                    type="submit"
                    disabled={surveySubmitting}
                    style={{
                      border: 'none',
                      background: 'linear-gradient(135deg, #db2c27 0%, #b82220 100%)',
                      color: '#fff',
                      borderRadius: '9px',
                      padding: '9px 10px',
                      fontSize: '0.84rem',
                      fontWeight: 700,
                      cursor: surveySubmitting ? 'default' : 'pointer',
                      opacity: surveySubmitting ? 0.75 : 1,
                    }}
                  >
                    {surveySubmitting ? 'Submitting...' : 'Submit Survey'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chatbot;
